/**
 * Short-lived discourse popups anchored to board slots when origin is known.
 * Gated by CombatFeedbackSettings.discourse (default Off — board owns the fight).
 */

import type { CombatLogEntry, LogOrigin } from "../game/combatLog";
import type { DiscourseLevel } from "../game/combatFeedback";

export type DiscourseKind = "lie" | "truth" | "strain" | "system";

function classify(msg: string): DiscourseKind {
  if (/^Truth:/.test(msg)) return "truth";
  if (/\babides\b/.test(msg) || /\bfell silent\b/.test(msg) || /\bfalls silent\b/.test(msg)) {
    return "truth";
  }
  if (/^[1-3]?[A-Za-z. ]+\d+:\d/.test(msg) && /[“"]/.test(msg)) return "truth";
  if (
    /\bstrain each other\b/.test(msg) ||
    /\bstrained\b/.test(msg) ||
    (/\bpresses\b/.test(msg) && !/\bpresses into\b/.test(msg))
  ) {
    return "strain";
  }
  if (
    /\benters the\b/.test(msg) ||
    /\bpresses into\b/.test(msg) ||
    /\bbreaches\b/.test(msg) ||
    /\bmultiplies\b/.test(msg) ||
    /→ “/.test(msg) ||
    /\):\s*“/.test(msg)
  ) {
    return "lie";
  }
  if (/\b: “/.test(msg)) return "lie";
  return "system";
}

function isDiscourseWorthy(msg: string): boolean {
  const m = msg.trim();
  if (!m) return false;
  if (/^Paused/.test(m) || /^Resumed/.test(m)) return false;
  if (/^Mission start/.test(m) || /^Revealed /.test(m)) return false;
  if (/^Victory ·/.test(m) || /^Defeat ·/.test(m) || /^Retreated ·/.test(m)) return false;
  if (/Not enough Spirit/.test(m)) return false;
  if (/Drew a verse/.test(m)) return false;
  if (/found no valid/.test(m) || /no legal slot/.test(m) || /no lie to/.test(m)) return false;
  if (/resume to answer/.test(m)) return false;
  if (/wear \d+%/.test(m) && /presses/.test(m)) return false;
  // Phase A: board arrows + land FX own these — no floating narration
  if (/\bpresses into\b/.test(m)) return false;
  if (/\bbreaches the\b/.test(m)) return false;
  if (/\bhammers the\b/.test(m)) return false;
  if (/\bwalks the path into\b/.test(m)) return false;
  if (/\bclears the way into\b/.test(m)) return false;
  if (/\bfalters on the path\b/.test(m)) return false;
  if (/\bmultiplies\b/.test(m)) return false;
  // Phase C: board FX own silence / strain / level steps
  if (/\bfell silent\b/.test(m) || /\bfalls silent\b/.test(m)) return false;
  if (/\bis no longer held\b/.test(m)) return false;
  if (/\bstrain each other\b/.test(m)) return false;
  if (/\bStill on\b/.test(m)) return false;
  // Level step lines: "Name (III → II) on Heart"
  if (/\([IVX]+ → [IVX0]/.test(m)) return false;
  return true;
}

const MAX_VISIBLE = 5;
const LIFE_MS = 2600;
const FADE_MS = 400;

/** Stack offset when several popups share a slot. */
const stackOffsetByKey = new Map<string, number>();

function originKey(o: LogOrigin | undefined): string {
  if (!o) return "fallback";
  return `${o.layer}:${o.slot ?? "row"}`;
}

function resolveAnchor(origin?: LogOrigin): { x: number; y: number } | null {
  const board = document.getElementById("board");
  if (!board) return null;

  if (origin) {
    if (origin.slot !== undefined) {
      const slot = board.querySelector(
        `.slot[data-layer="${origin.layer}"][data-slot="${origin.slot}"]`,
      ) as HTMLElement | null;
      if (slot) {
        const r = slot.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + 4 };
      }
    }
    const row = board.querySelector(
      `.layer[data-layer="${origin.layer}"]`,
    ) as HTMLElement | null;
    if (row) {
      const r = row.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height * 0.35 };
    }
  }

  // Fallback: center of board
  const br = board.getBoundingClientRect();
  return { x: br.left + br.width / 2, y: br.top + br.height * 0.45 };
}

export class DiscoursePopups {
  private root: HTMLElement;
  private stack: HTMLElement[] = [];
  /** Default off — board FX + end log cover combat story. */
  private level: DiscourseLevel = "off";
  /** Screen-reader only criticals when visual popups are off. */
  private criticalLive: HTMLElement | null = null;

  constructor(host?: HTMLElement | null) {
    let el = host ?? document.getElementById("discourse-popups");
    if (!el) {
      el = document.createElement("div");
      el.id = "discourse-popups";
      document.body.appendChild(el);
    }
    el.className = "discourse-popups discourse-popups-anchored";
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-relevant", "additions");
    this.root = el;
    this.syncAriaLive();
  }

  setLevel(level: DiscourseLevel): void {
    if (this.level === level) return;
    this.level = level;
    if (level === "off") this.clear();
    this.syncAriaLive();
  }

  getLevel(): DiscourseLevel {
    return this.level;
  }

  private syncAriaLive(): void {
    // Visual channel: polite when text is on; off when silent
    if (this.level === "off") {
      this.root.setAttribute("aria-live", "off");
      this.ensureCriticalLive();
    } else {
      this.root.setAttribute("aria-live", "polite");
    }
  }

  /** Rare critical announce when discourse popups are off (a11y). */
  private ensureCriticalLive(): void {
    if (this.criticalLive) return;
    const el = document.createElement("div");
    el.id = "discourse-critical-live";
    el.className = "sr-only";
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-relevant", "additions text");
    document.body.appendChild(el);
    this.criticalLive = el;
  }

  private announceCritical(msg: string): void {
    this.ensureCriticalLive();
    if (!this.criticalLive) return;
    // Clear then set so consecutive announcements still fire
    this.criticalLive.textContent = "";
    requestAnimationFrame(() => {
      if (this.criticalLive) this.criticalLive.textContent = msg;
    });
  }

  clear(): void {
    this.root.replaceChildren();
    this.stack = [];
    stackOffsetByKey.clear();
  }

  push(msg: string, entry?: CombatLogEntry): void {
    if (!isDiscourseWorthy(msg)) return;
    const kind = (entry?.kind as DiscourseKind) || classify(msg);
    if (kind === "system" && !entry?.origin) {
      // Keep unanchored system lines quiet unless they have a place
      if (!/[“"]/.test(msg) && !/\babides\b/.test(msg)) return;
    }

    if (this.level === "off") {
      // Optional SR-only for rare sacred lines (truth with quote / abides)
      if (
        kind === "truth" &&
        (/[“"]/.test(msg) || /\babides\b/.test(msg) || /\bRedeemed\b/.test(msg))
      ) {
        this.announceCritical(msg);
      }
      return;
    }

    if (this.level === "minimal") {
      // Sacred / claim only — skip strain & generic system chatter
      if (kind === "strain") return;
      if (kind === "system") return;
      // Lie claims and truth quotes / abides
      const hasQuote = /[“"]/.test(msg);
      if (kind === "lie" && !hasQuote) return;
      if (kind === "truth" && !hasQuote && !/\babides\b/.test(msg)) return;
    }

    const origin = entry?.origin;
    const anchor = resolveAnchor(origin);
    if (!anchor) return;

    const key = originKey(origin);
    const stackN = stackOffsetByKey.get(key) ?? 0;
    stackOffsetByKey.set(key, stackN + 1);

    const bubble = document.createElement("div");
    bubble.className = `discourse-popup kind-${kind} anchored`;
    bubble.setAttribute("role", "status");

    const label = document.createElement("span");
    label.className = "discourse-label";
    label.textContent =
      kind === "lie"
        ? "Lie"
        : kind === "truth"
          ? "Word"
          : kind === "strain"
            ? "Clash"
            : "…";

    const body = document.createElement("p");
    body.className = "discourse-body";
    body.textContent = msg;

    bubble.append(label, body);
    this.root.appendChild(bubble);
    this.stack.push(bubble);

    // Place above slot center; stack upward when busy
    const lift = stackN * 52;
    bubble.style.left = `${anchor.x}px`;
    bubble.style.top = `${anchor.y - lift}px`;

    requestAnimationFrame(() => bubble.classList.add("visible"));

    while (this.stack.length > MAX_VISIBLE) {
      const old = this.stack.shift();
      if (old) this.dismiss(old, true, key);
    }

    window.setTimeout(() => this.dismiss(bubble, false, key), LIFE_MS);
  }

  private dismiss(el: HTMLElement, instant: boolean, key?: string): void {
    const idx = this.stack.indexOf(el);
    if (idx >= 0) this.stack.splice(idx, 1);
    if (key) {
      const n = stackOffsetByKey.get(key) ?? 1;
      stackOffsetByKey.set(key, Math.max(0, n - 1));
    }
    if (!el.isConnected) return;
    if (instant) {
      el.remove();
      return;
    }
    el.classList.remove("visible");
    el.classList.add("leaving");
    window.setTimeout(() => el.remove(), FADE_MS);
  }
}
